/**
 * templatePreview.js
 * Shared utility for rendering template HTML with brand/font overrides.
 * Used by TemplatesPage, TemplateDetailPage, and TemplateEditorPage.
 */

/**
 * @param {string} html - Raw template HTML string
 * @param {object} template - Template object from templates.js registry
 * @param {string} baseDir - Full URL or relative path of the template directory
 * @returns {string} Processed HTML ready for iframe srcDoc
 */
export function processTemplateHtml(html, template, baseDir) {
  // 1. Hide preloaders that self-dismiss on real page load but not in iframes
  html = html.replace(/<\/head>/i, `<style>.preloader,.loader,.loading-screen{display:none!important;opacity:0!important;visibility:hidden!important}</style></head>`);

  // 2. Inject <base> tag so relative asset paths resolve correctly
  let baseTag = '';
  if (baseDir && baseDir.startsWith('http')) {
    baseTag = `<base href="${baseDir}">`;
  } else if (baseDir) {
    const dir = baseDir.startsWith('/') ? baseDir.substring(1) : baseDir;
    baseTag = `<base href="${window.location.origin}/${dir}">`;
  }

  // 3. Link click interceptor — prevent iframe navigation, relay to parent
  const navScript = `<script>document.addEventListener('click',function(e){
    var a=e.target.closest('a');
    if(!a)return;
    e.preventDefault();
    var href=a.getAttribute('href');
    if(href&&href!=='#'&&!href.startsWith('#')&&!href.startsWith('javascript:')&&!href.startsWith('mailto:')&&!href.startsWith('tel:')){
      window.parent.postMessage({type:'orbis_preview_nav',href:href},'*');
    }
  });</script>`;

  html = html.replace(/<head(\s[^>]*)?>/i, `<head$1>\n  ${baseTag}\n  ${navScript}`);

  // 4. Rewrite absolute paths so <base> tag resolves static assets
  html = html.replace(/(href|src|content)="\/(_next|assets|images|css|js|fonts)\//g, '$1="$2/');

  // 5. Apply Orbis brand token placeholders (used by custom Orbis templates)
  const { colors, fonts } = template.defaultBrandKit;
  html = html.replace(/BRAND_PRIMARY/g, colors.primary);
  html = html.replace(/BRAND_SECONDARY/g, colors.secondary);
  html = html.replace(/BRAND_ACCENT/g, colors.accent);
  html = html.replace(/BRAND_BG/g, colors.background);
  html = html.replace(/BRAND_TEXT/g, colors.text);

  // 6. Apply font placeholders
  const h = fonts.heading.replace(/ /g, '+');
  const b = fonts.body.replace(/ /g, '+');
  const fontsUrl =
    h === b
      ? `https://fonts.googleapis.com/css2?family=${h}:wght@300;400;500;600;700&display=swap`
      : `https://fonts.googleapis.com/css2?family=${h}:wght@300;400;500;600;700&family=${b}:wght@300;400;500;600;700&display=swap`;
  html = html.replace(/GOOGLE_FONTS_URL/g, fontsUrl);
  html = html.replace(/FONT_HEADING/g, fonts.heading);
  html = html.replace(/FONT_BODY/g, fonts.body);

  // 7. Envato / hardcoded-CSS templates: inject brand override styles
  const isEnvatoTemplate = !html.includes('var(--heading-font)') && !html.includes('FONT_HEADING');
  if (isEnvatoTemplate) {
    const brandOverride = `
    <link rel="preconnect" href="https://fonts.googleapis.com">
    <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
    <link href="${fontsUrl}" rel="stylesheet">
    <style id="orbis-brand-override">
      :root {
        --font-heading: '${fonts.heading}', sans-serif;
        --font-body: '${fonts.body}', sans-serif;
        --brand-primary: ${colors.primary};
        --brand-secondary: ${colors.secondary};
        --brand-accent: ${colors.accent};
        --brand-bg: ${colors.background};
        --brand-text: ${colors.text};
      }
      h1,h2,h3,h4,h5,h6,[class*="title"],[class*="heading"],[class*="headline"] {
        font-family: '${fonts.heading}', sans-serif !important;
      }
      body,p,li,span,a,input,textarea,button {
        font-family: '${fonts.body}', sans-serif !important;
      }
      body { background-color: ${colors.background} !important; color: ${colors.text} !important; }
      .btn-primary,.btn--primary,.theme-btn,[class*="btn-primary"],a.btn {
        background-color: ${colors.primary} !important;
        border-color: ${colors.primary} !important;
      }
      a:hover,.section-title span,.section-tag { color: ${colors.primary} !important; }
      [class*="highlight"],[class*="accent"],.badge,.tag {
        background-color: ${colors.accent} !important;
        border-color: ${colors.accent} !important;
      }
    </style>`;
    html = html.replace(/<\/head>/i, `${brandOverride}\n</head>`);

    // Direct hex color swap: replace template's default hex colors with user brand
    const defaults = template.defaultBrandKit?.colors || {};
    const swaps = [
      [defaults.primary, colors.primary],
      [defaults.secondary, colors.secondary],
      [defaults.accent, colors.accent],
      [defaults.background, colors.background],
      [defaults.text, colors.text],
    ];
    for (const [from, to] of swaps) {
      if (from && to && from.toLowerCase() !== to.toLowerCase()) {
        html = html.replaceAll(from, to);
        html = html.replaceAll(from.toLowerCase(), to);
        html = html.replaceAll(from.toUpperCase(), to);
      }
    }
  }

  // 8. Strip Orbis slot / image placeholders from preview HTML
  html = html.replace(/ORBIS_SLOT:\w+:\w+/g, '');
  html = html.replace(/<!--\s*ORBIS_SLOT:\w+\s*-->/g, '');
  html = html.replace(/<!--\s*\/ORBIS_SLOT\s*-->/g, '');
  html = html.replace(/ORBIS_IMAGE:\w+/g, 'https://placehold.co/800x400/1a1a2e/ffffff?text=Image');

  return html;
}

/**
 * Fetch a template page URL and return processed HTML for iframe display.
 * @param {string} url - Full URL to the template HTML file
 * @param {object} template - Template object from templates.js
 * @returns {Promise<string>} Processed HTML string
 */
export async function fetchAndProcessTemplate(url, template) {
  const resp = await fetch(url);
  if (!resp.ok) throw new Error(`Failed to fetch template: ${resp.status}`);
  let html = await resp.text();
  const baseDir = url.substring(0, url.lastIndexOf('/') + 1);
  return processTemplateHtml(html, template, baseDir);
}
